// Shared plumbing for the identity workflows (invitation, recovery, two-step enrollment): one
// snapshot store per flow, a generation guard so late answers never land, the session/account
// key that clears secrets when the signed-in account changes, and capability loading.
import type { ApiClient, DataEnvelope } from '../lib/api.ts';
import { isAbortError, isApiRequestError } from '../lib/api.ts';
import { type IdentityCapabilities, parseCapabilities } from './contract.ts';

export interface FlowOptions {
  api: Pick<ApiClient, 'request'>;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

/** Safe server fields only, for Technical details. Never a token, challenge, seed, code or password. */
export interface FlowDiagnostic {
  code: string;
  requestId: string | null;
}

export type FailureKind =
  | 'unavailable' // 404 / 503 / mail not configured: the server doesn't offer this (yet)
  | 'invalid' // workflow_invalid: link, password, code or step no longer valid (deliberately generic)
  | 'mismatch' // identity_mismatch: signed in as someone else
  | 'member' // membership_exists
  | 'session' // the browser session or its CSRF token changed
  | 'limited' // rate limited
  | 'unknown' // a change may or may not have happened (no answer to a POST)
  | 'network' // a read got no answer
  | 'input' // 422: the server refused a field
  | 'denied' // 403: not allowed (role, MFA, origin)
  | 'other';

export function classifyFailure(error: unknown): FailureKind {
  if (!isApiRequestError(error)) return 'other';
  if (error.outcomeUnknown) return 'unknown';
  if (error.status === 0) return 'network';
  if (error.status === 404 || error.status === 503 || error.code === 'identity_mail_unconfigured') return 'unavailable';
  if (error.code === 'workflow_invalid') return 'invalid';
  if (error.code === 'identity_mismatch') return 'mismatch';
  if (error.code === 'membership_exists') return 'member';
  if (error.status === 401 || error.code === 'browser_session_required' || error.code === 'csrf_invalid') return 'session';
  if (error.status === 429 || error.code === 'workflow_limited') return 'limited';
  if (error.status === 422) return 'input';
  if (error.status === 403) return 'denied';
  return 'other';
}

export function diagnosticOf(error: unknown): FlowDiagnostic {
  return isApiRequestError(error) ? { code: error.code, requestId: error.requestId } : { code: 'unexpected_error', requestId: null };
}

export type CapabilityResult =
  | { kind: 'ready'; value: IdentityCapabilities }
  /** The endpoint is missing (404) or the service is unavailable (503): identity workflows aren't on this server. */
  | { kind: 'missing'; diagnostic: FlowDiagnostic }
  /** A read failure or an unreadable answer: try again. */
  | { kind: 'error'; diagnostic: FlowDiagnostic };

export async function loadIdentityCapabilities(api: Pick<ApiClient, 'request'>, signal?: AbortSignal): Promise<CapabilityResult> {
  try {
    const env = await api.request<DataEnvelope<unknown>>('/identity/capabilities', signal ? { signal } : {});
    const value = parseCapabilities(typeof env === 'object' && env !== null ? env.data : undefined);
    return value ? { kind: 'ready', value } : { kind: 'error', diagnostic: { code: 'invalid_response', requestId: null } };
  } catch (error) {
    if (isAbortError(error)) throw error;
    return classifyFailure(error) === 'unavailable' ? { kind: 'missing', diagnostic: diagnosticOf(error) } : { kind: 'error', diagnostic: diagnosticOf(error) };
  }
}

export abstract class Flow<S extends { phase: string }> {
  private snap: S;
  private readonly listeners = new Set<() => void>();
  private generation = 0;
  private inflight: AbortController | null = null;
  private userKey: string | null = null;
  private csrf: string | null = null;
  private mounted = false;
  private wipeTimer: unknown = null;
  protected readonly api: Pick<ApiClient, 'request'>;
  protected readonly now: () => number;
  protected readonly setTimer: (fn: () => void, ms: number) => unknown;
  protected readonly clearTimer: (handle: unknown) => void;

  constructor(options: FlowOptions, initial: S) {
    this.snap = initial;
    this.api = options.api;
    this.now = options.now ?? Date.now;
    this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = options.clearTimer ?? (handle => clearTimeout(handle as ReturnType<typeof setTimeout>));
  }

  getSnapshot = (): S => this.snap;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  /**
   * The browser session the page runs under. A different account clears every secret; a new
   * session for the same account (new CSRF token) invalidates anything bound to the old session.
   * No POST is sent before the normal session bootstrap has supplied a CSRF token.
   */
  setSession(userId: string | null, csrf: string | null): void {
    const key = userId ?? '';
    if (this.userKey === null) {
      this.userKey = key;
      this.csrf = csrf;
      if (csrf && this.mounted) this.onSessionReady();
      return;
    }
    if (key !== this.userKey) {
      this.userKey = key;
      this.csrf = csrf;
      this.cancel();
      this.onAccountChanged();
      return;
    }
    if (csrf === this.csrf) return;
    const had = this.csrf;
    this.csrf = csrf;
    if (!csrf) return;
    if (had) this.onSessionRotated();
    else if (this.mounted) this.onSessionReady();
  }

  /** Mount. Pairs with detach(); a StrictMode remount within the same tick keeps the flow. */
  attach(): void {
    this.mounted = true;
    if (this.wipeTimer !== null) this.clearTimer(this.wipeTimer);
    this.wipeTimer = null;
    this.onAttach();
  }

  /** Unmount: stop work now and forget every secret unless the page is mounted again at once. */
  detach(): void {
    this.mounted = false;
    this.cancel();
    if (this.wipeTimer !== null) this.clearTimer(this.wipeTimer);
    this.wipeTimer = this.setTimer(() => {
      this.wipeTimer = null;
      if (!this.mounted) this.wipe();
    }, 0);
  }

  /** Clears every secret this flow holds. */
  abstract wipe(): void;
  protected abstract onAttach(): void;
  protected abstract onSessionReady(): void;
  protected abstract onAccountChanged(): void;
  protected abstract onSessionRotated(): void;

  protected get sessionReady(): boolean {
    return !!this.csrf && this.userKey !== null;
  }
  protected get attached(): boolean {
    return this.mounted;
  }
  protected get busy(): boolean {
    return this.inflight !== null;
  }
  protected get signedIn(): boolean {
    return !!this.userKey;
  }

  /** Starts a request. Whatever was in flight is abandoned and its answer will be ignored. */
  protected begin(): { gen: number; signal: AbortSignal } {
    this.cancel();
    const controller = new AbortController();
    this.inflight = controller;
    return { gen: this.generation, signal: controller.signal };
  }

  /** True while `gen` is still the current request (it keeps the in-flight marker). */
  protected current(gen: number): boolean {
    return gen === this.generation;
  }

  /** True while `gen` is still the current request. Ends the in-flight marker when it is. */
  protected landed(gen: number): boolean {
    if (gen !== this.generation) return false;
    this.inflight = null;
    return true;
  }

  protected cancel(): void {
    this.generation += 1;
    this.inflight?.abort();
    this.inflight = null;
  }

  protected set(patch: Partial<S>): void {
    this.snap = { ...this.snap, ...patch };
    for (const l of [...this.listeners]) l();
  }
}

export interface FragmentSource {
  take(kind: 'invite' | 'recover'): { token: string | null } | null;
}
